import { Box, LinearProgress, Step, StepLabel, Stepper, Typography } from '@mui/material';
import type { IOnboardingProgressComponentProperties } from '@sharedInterfaces/IEmployeeTalkTemplate';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import RowElement from '@src/App/Components/General/FormularElements/RowElement/RowElement';
import type { AppState } from '@store/store';
import React from 'react';
import { useSelector } from 'react-redux';

import type { ITemplateComponentProps } from '../TemplateEditor/types';

import ETTBaseTemplateComponent from './ETTBaseTemplateComponent';

const ETTOnboardingProgressComponent: React.FC<ITemplateComponentProps> = (props) => {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).employeeTalk;

    const properties = props.component.properties as IOnboardingProgressComponentProperties;

    // Mock progress data - in real implementation this would come from context
    const currentStep = 3;
    const totalSteps = 6;
    const progressPercentage = Math.round((currentStep / totalSteps) * 100);

    const steps = [
        'Willkommen',
        'Persönliche Daten',
        'CV Upload',
        'Skills & Kompetenzen',
        'Ziele & Erwartungen',
        'Abschluss',
    ];

    const renderEditContent = () => (
        <>
            <RowElement title="Prozentsatz anzeigen">
                <input
                    type="checkbox"
                    checked={properties.showPercentage}
                    onChange={(e) =>
                        props.onEdit?.({
                            ...props.component,
                            properties: {
                                ...properties,
                                showPercentage: e.target.checked,
                            },
                        })
                    }
                />
            </RowElement>
            <RowElement title="Schritt-Namen anzeigen">
                <input
                    type="checkbox"
                    checked={properties.showStepNames}
                    onChange={(e) =>
                        props.onEdit?.({
                            ...props.component,
                            properties: {
                                ...properties,
                                showStepNames: e.target.checked,
                            },
                        })
                    }
                />
            </RowElement>
        </>
    );

    const renderViewContent = () => (
        <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" sx={{ mr: 2 }}>
                    Schritt {currentStep} von {totalSteps}
                </Typography>
                {properties.showPercentage && (
                    <Typography variant="body2" sx={{ color: 'primary.main', fontWeight: 500 }}>
                        {progressPercentage}% abgeschlossen
                    </Typography>
                )}
            </Box>

            <LinearProgress
                variant="determinate"
                value={progressPercentage}
                sx={{ height: 8, borderRadius: 4, mb: 3 }}
            />

            {properties.showStepNames && (
                <Stepper
                    activeStep={currentStep - 1}
                    alternativeLabel
                    sx={{
                        '& .MuiStepLabel-label': {
                            fontSize: '0.75rem',
                        },
                    }}
                >
                    {steps.map((label, index) => (
                        <Step key={index} completed={index < currentStep - 1}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            )}
        </Box>
    );

    return (
        <ETTBaseTemplateComponent
            {...props}
            renderEditContent={renderEditContent}
            renderViewContent={renderViewContent}
        />
    );
};

export default ETTOnboardingProgressComponent;
